import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faPhone, 
  faEnvelope, 
  faClock,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/theme';
import { Container, Section, Button } from '../styles/components';
import PageHero from '../components/layout/PageHero';

const ContactSection = styled(Section)`
  padding: ${theme.spacing.xxl} 0;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ContactForm = styled.form`
  background: ${theme.colors.background};
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
`;

const FormTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: ${theme.spacing.lg};
  font-family: ${theme.fonts.heading};
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: ${theme.spacing.xs};
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.gray};
  border-radius: ${theme.borderRadius.small};
  font-family: inherit;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.2);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.gray};
  border-radius: ${theme.borderRadius.small};
  font-family: inherit;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.2);
  }
`;

const SubmitButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  
  svg {
    font-size: 1rem;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const InfoTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.fonts.heading};
`;

const InfoCard = styled.div`
  background: ${theme.colors.background};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.small};
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.md};
  transition: ${theme.transitions.medium};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${theme.shadows.medium};
    
    svg {
      color: ${theme.colors.primary};
    }
  }
  
  svg {
    font-size: 1.5rem;
    color: ${theme.colors.secondary};
    transition: ${theme.transitions.medium};
  }
`;

const InfoContent = styled.div`
  h3 {
    margin-bottom: ${theme.spacing.xs};
    font-size: 1.2rem;
  }
  
  p {
    color: ${theme.colors.textLight};
    line-height: 1.6;
  }
  
  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const MapSection = styled(Section)`
  padding-top: 0;
`;

const MapContainer = styled.div`
  height: 400px;
  border-radius: ${theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${theme.shadows.medium};
  
  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const SuccessMessage = styled.div`
  background-color: #e6f7e6;
  color: #2e7d32;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.small};
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real application, you would send the form data to a server
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    
    // Reset success message after 5 seconds
    setTimeout(() => {
      setIsSubmitted(false);
    }, 5000);
  };
  
  return (
    <>
      <PageHero 
        title="Contact Us" 
        subtitle="We'd love to hear from you. Reach out to us with any questions or feedback."
        height="40vh"
      />
      
      <ContactSection>
        <Container>
          <ContactGrid>
            <ContactForm onSubmit={handleSubmit}>
              <FormTitle>Send Us a Message</FormTitle>
              
              {isSubmitted && (
                <SuccessMessage>
                  Thank you for your message! We'll get back to you soon.
                </SuccessMessage>
              )}
              
              <FormGroup>
                <FormLabel htmlFor="name">Your Name</FormLabel>
                <FormInput 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="email">Email Address</FormLabel>
                <FormInput 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="subject">Subject</FormLabel>
                <FormInput 
                  type="text" 
                  id="subject" 
                  name="subject" 
                  value={formData.subject}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="message">Your Message</FormLabel>
                <FormTextarea 
                  id="message" 
                  name="message" 
                  value={formData.message}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <SubmitButton type="submit" variant="primary" size="large">
                Send Message <FontAwesomeIcon icon={faPaperPlane} />
              </SubmitButton>
            </ContactForm>
            
            <ContactInfo>
              <InfoTitle>Get in Touch</InfoTitle>
              
              <InfoCard>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <InfoContent>
                  <h3>Visit Us</h3>
                  <p>
                    Alherz Perfume Flagship Store<br />
                    Seef Mall, Shop #123<br />
                    Manama, Bahrain
                  </p>
                </InfoContent>
              </InfoCard>
              
              <InfoCard>
                <FontAwesomeIcon icon={faPhone} />
                <InfoContent>
                  <h3>Call Us</h3>
                  <p>
                    <a href="tel:+97317123456">+973 1712 3456</a><br />
                    <a href="tel:+97336123456">+973 3612 3456</a> (WhatsApp)
                  </p>
                </InfoContent>
              </InfoCard>
              
              <InfoCard>
                <FontAwesomeIcon icon={faEnvelope} />
                <InfoContent>
                  <h3>Email Us</h3>
                  <p>
                    <a href="mailto:info@alherzperfume.com">info@alherzperfume.com</a><br />
                    <a href="mailto:support@alherzperfume.com">support@alherzperfume.com</a>
                  </p>
                </InfoContent>
              </InfoCard>
              
              <InfoCard>
                <FontAwesomeIcon icon={faClock} />
                <InfoContent>
                  <h3>Opening Hours</h3>
                  <p>
                    Monday - Saturday: 10:00 AM - 10:00 PM<br />
                    Friday: 2:00 PM - 10:00 PM
                  </p>
                </InfoContent>
              </InfoCard>
            </ContactInfo>
          </ContactGrid>
        </Container>
      </ContactSection>
      
      <MapSection>
        <Container>
          <MapContainer>
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7151.677210274963!2d50.57565678678702!3d26.23646368339212!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e49a58b0e7fcf9f%3A0x5b5c39e54b512a86!2sSeef%20Mall!5e0!3m2!1sen!2sus!4v1651234567890!5m2!1sen!2sus" 
              allowFullScreen 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Alherz Perfume Location"
            />
          </MapContainer>
        </Container>
      </MapSection>
    </>
  );
};

export default ContactPage; 